import { saveMarketplaceToken } from "@/api/saveMarketpaceToken";
import { disconnectKaspi } from "@/api/kaspiApi/disconnectKaspi";
import { savePreferredMarketplace, type PreferredMarketplace } from "@/api/savePreferredMarketplace";
import { saveThemePreference, type UiTheme } from "@/api/saveThemePreference";
import { defineStore } from "pinia";
import { ref } from "vue";

interface CardGeniusAccountProfile {
  profileId: string;
  userId: string;
}

export type MarketplaceKey = "wildberries" | "ozon" | "amazon" | "shopify" | "kaspi";
type AuthProviderKey = "google" | "apple";

interface AuthProviderState {
  connected: boolean;
  email: string;
}

export interface MarketplaceConnection {
  connected: boolean;
  token: string;
  sellerId?: string;
}

export interface WbSyncStatus {
  status: "pending" | "running" | "finished" | "failed";
  total: number;
  loaded: number;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
}

const THEME_STORAGE_KEY = "cardgenius.theme";

function applyThemeToDom(theme: UiTheme) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

function readThemeFromLocalStorage(): UiTheme | null {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored === "dark" || stored === "light" ? stored : null;
}

function writeThemeToLocalStorage(theme: UiTheme) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(THEME_STORAGE_KEY, theme);
}

function systemPreferredTheme(): UiTheme {
  if (typeof window === "undefined") return "light";
  const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  return prefersDark ? "dark" : "light";
}

function pickBool(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

export const useAuthStore = defineStore("auth", () => {
  const cardGeniusAccountProfile = ref<CardGeniusAccountProfile | null>(null);
  const geniBalance = ref(0);
  const accountName = ref("");
  const accountPhone = ref("");
  const accountEmail = ref("");
  const signInProviders = ref<Record<AuthProviderKey, AuthProviderState>>({
    google: { connected: false, email: "" },
    apple: { connected: false, email: "" },
  });
  const marketplaceConnections = ref<Record<MarketplaceKey, MarketplaceConnection>>({
    wildberries: { connected: false, token: "", sellerId: "" },
    ozon: { connected: false, token: "" },
    amazon: { connected: false, token: "" },
    shopify: { connected: false, token: "" },
    kaspi: { connected: false, token: "" },
  });

  const uiTheme = ref<UiTheme>("light");
  const preferredMarketplace = ref<PreferredMarketplace>("wb");
  // Background WB goods import progress, written by card_genius_service into
  // AccountProfile.metadata.syncStatus (arrives here as field_19.syncStatus).
  const wbSyncStatus = ref<WbSyncStatus | null>(null);
  const accountShowsPasswordSection = ref(true);
  const onboardingCompletedAt = ref<string | null>(null);

  function pickEmail(value: unknown): string {
    return typeof value === "string" && value.includes("@") ? value.trim() : "";
  }

  function pickString(value: unknown): string {
    return typeof value === "string" ? value.trim() : "";
  }

  function resolveAccountShowsPasswordSection(profile: any, field19: Record<string, unknown>): boolean {
    const rp = (
      pickString(profile?.registrationProvider) ||
      pickString(field19.registrationProvider) ||
      (field19.signUp && typeof field19.signUp === "object"
        ? pickString((field19.signUp as Record<string, unknown>).provider)
        : "")
    ).toLowerCase();
    if (rp === "google") return false;

    const authBlock =
      field19.auth && typeof field19.auth === "object" ? (field19.auth as Record<string, unknown>) : null;

    const hasPassword =
      pickBool(profile?.hasPassword) ??
      pickBool(field19.hasPassword) ??
      pickBool(authBlock?.hasPassword) ??
      pickBool(authBlock?.passwordSet);

    if (hasPassword === false) return false;
    if (hasPassword === true) return true;

    const identities =
      field19.identities && typeof field19.identities === "object"
        ? (field19.identities as Record<string, unknown>)
        : null;
    if (identities && pickBool(identities.password) === false) return false;

    const googleMetaObj =
      field19.google && typeof field19.google === "object" ? (field19.google as Record<string, unknown>) : null;
    if (googleMetaObj) {
      if (
        pickBool(googleMetaObj.oauthOnly) === true ||
        pickBool(googleMetaObj.oauthSignup) === true ||
        pickBool(googleMetaObj.registrationOnly) === true
      ) {
        return false;
      }
    }

    // OAuth-first Google accounts often omit explicit flags; hide password UI when Google is linked and no phone login identity exists yet.
    if (hasPassword === undefined && rp === "") {
      const g =
        field19.google && typeof field19.google === "object" ? (field19.google as Record<string, unknown>) : null;
      const googleConnected = Boolean(
        g?.connected ?? field19.googleConnected ?? (typeof g?.email === "string" && g.email.includes("@")),
      );
      const nestedProfile =
        field19.profile && typeof field19.profile === "object" ? (field19.profile as Record<string, unknown>) : null;
      const phoneFromProfile =
        pickString(profile?.phone) || pickString(nestedProfile?.phone) || pickString(field19.phone);
      if (googleConnected && !phoneFromProfile) {
        return false;
      }
    }

    return true;
  }

  function setCardGeniusAccountProfile(profile: any) {
    const field19 = profile?.field_19 ?? {};
    const profileMeta =
      field19?.profile && typeof field19.profile === "object" && field19.profile !== null ? field19.profile : {};
    const googleMeta = field19?.google ?? profile?.google ?? {};
    const appleMeta = field19?.apple ?? profile?.apple ?? {};

    const resolvedUserId = String(profile?.userId ?? profile?.id ?? profile?.profile_id ?? "").trim();
    cardGeniusAccountProfile.value = {
      profileId: String(profile?.profile_id ?? profile?.id ?? "").trim(),
      userId: resolvedUserId,
    };
    geniBalance.value = Number(profile.geni_balance ?? 0);
    accountName.value = pickString(profileMeta?.name) || pickString(field19?.name);
    accountPhone.value = pickString(profile?.phone) || pickString(profileMeta?.phone) || pickString(field19?.phone);
    accountEmail.value = pickEmail(profile?.email) || pickEmail(profileMeta?.email) || pickEmail(field19?.email);
    signInProviders.value.google.email = pickEmail(googleMeta?.email) || pickEmail(field19?.googleEmail);
    signInProviders.value.apple.email = pickEmail(appleMeta?.email) || pickEmail(field19?.appleEmail);
    signInProviders.value.google.connected = Boolean(
      googleMeta?.connected ?? field19?.googleConnected ?? signInProviders.value.google.email,
    );
    signInProviders.value.apple.connected = Boolean(
      appleMeta?.connected ?? field19?.appleConnected ?? signInProviders.value.apple.email,
    );

    const profileTheme = field19?.theme;
    const resolvedTheme: UiTheme =
      profileTheme === "dark" || profileTheme === "light"
        ? profileTheme
        : readThemeFromLocalStorage() ?? systemPreferredTheme();
    uiTheme.value = resolvedTheme;
    applyThemeToDom(resolvedTheme);
    writeThemeToLocalStorage(resolvedTheme);

    for (const key of Object.keys(marketplaceConnections.value) as MarketplaceKey[]) {
      const token = field19?.[key] || "";
      marketplaceConnections.value[key].token = token;
      // Kaspi считается «подключённым» только когда есть и токен, и merchantId магазина —
      // без merchantId анализ позиций/импорт товаров невозможны, поэтому в UI не показываем
      // привязку, пока merchantId не получен (авто из заказов или ручной ввод).
      if (key === "kaspi") {
        const merchantId = field19?.kaspiMerchantId || "";
        marketplaceConnections.value[key].connected = token !== "" && merchantId !== "";
      } else {
        marketplaceConnections.value[key].connected = token !== "";
      }
    }
    const profileMarketplace = field19?.preferredMarketplace;
    if (
      profileMarketplace === "wb" ||
      profileMarketplace === "ozon" ||
      profileMarketplace === "kaspi" ||
      profileMarketplace === "flip" ||
      profileMarketplace === "olx"
    ) {
      preferredMarketplace.value = profileMarketplace;
    }

    accountShowsPasswordSection.value = resolveAccountShowsPasswordSection(profile, field19 as Record<string, unknown>);

    const rawOnboarding = profile?.onboardingCompletedAt;
    onboardingCompletedAt.value =
      typeof rawOnboarding === "string" && rawOnboarding.trim() !== "" ? rawOnboarding : null;

    const rawSyncStatus = field19?.syncStatus;
    if (rawSyncStatus && typeof rawSyncStatus === "object") {
      const s = rawSyncStatus as Record<string, unknown>;
      const status = s.status;
      if (status === "pending" || status === "running" || status === "finished" || status === "failed") {
        wbSyncStatus.value = {
          status,
          total: Number(s.total ?? 0),
          loaded: Number(s.loaded ?? 0),
          startedAt: typeof s.startedAt === "string" ? s.startedAt : undefined,
          finishedAt: typeof s.finishedAt === "string" ? s.finishedAt : undefined,
          error: typeof s.error === "string" ? s.error : undefined,
        };
      } else {
        wbSyncStatus.value = null;
      }
    } else {
      wbSyncStatus.value = null;
    }
  }

  function setOnboardingCompletedAt(completedAt: string) {
    onboardingCompletedAt.value = completedAt;
  }

  function setGeniBalance(value: number) {
    geniBalance.value = value;
  }

  async function setTheme(theme: UiTheme) {
    uiTheme.value = theme;
    applyThemeToDom(theme);
    writeThemeToLocalStorage(theme);

    // Save to profile when possible; otherwise local storage remains the source of truth.
    if (!cardGeniusAccountProfile.value?.profileId) {
      return;
    }

    try {
      await saveThemePreference(theme);
    } catch (e) {
      // Intentionally ignore: we keep local theme and avoid blocking UI.
      console.error(e);
    }
  }

  async function connectMarketplace(key: MarketplaceKey, payload: { token: string; sellerId?: string }) {
    const current = marketplaceConnections.value[key];
    await saveMarketplaceToken(key, payload.token);

    marketplaceConnections.value[key] = {
      ...current,
      token: payload.token,
      sellerId: payload.sellerId ?? current.sellerId,
      connected: true,
    };

    // Connecting a WB token kicks off a background goods import in card_genius_service.
    // Optimistically mark sync as pending so the goods page starts polling for progress
    // immediately, before the next profile fetch confirms the backend-written status.
    if (key === "wildberries" && payload.token.trim()) {
      wbSyncStatus.value = {
        status: "pending",
        total: 0,
        loaded: 0,
        startedAt: new Date().toISOString(),
      };
    }
  }

  async function disconnectMarketplace(key: MarketplaceKey) {
    const current = marketplaceConnections.value[key];
    // Kaspi хранит токен в metadata.kaspi и НЕ использует общую колонку marketplaceToken.
    // Обнулять её (как делает saveMarketplaceToken) нельзя — это сбросило бы токен WB.
    if (key === "kaspi") {
      await disconnectKaspi();
    } else {
      await saveMarketplaceToken(key, "");
    }

    marketplaceConnections.value[key] = {
      ...current,
      token: "",
      sellerId: current.sellerId !== undefined ? "" : undefined,
      connected: false,
    };
  }

  async function setPreferredMarketplace(marketplace: PreferredMarketplace) {
    preferredMarketplace.value = marketplace;
    if (!cardGeniusAccountProfile.value?.profileId) {
      return;
    }
    try {
      await savePreferredMarketplace(marketplace);
    } catch (error) {
      console.error(error);
    }
  }

  return {
    setCardGeniusAccountProfile,
    cardGeniusAccountProfile,
    accountName,
    accountPhone,
    accountEmail,
    signInProviders,
    marketplaceConnections,
    connectMarketplace,
    disconnectMarketplace,
    uiTheme,
    setTheme,
    preferredMarketplace,
    setPreferredMarketplace,
    onboardingCompletedAt,
    setOnboardingCompletedAt,
    geniBalance,
    setGeniBalance,
    accountShowsPasswordSection,
    wbSyncStatus,
  };
});
